import React, { useMemo, useState } from "react";
import { AlertCircle, MapPin, Package, Truck } from "@semo/icons";
import { Skeleton } from "@semo/shared-ui";
import { toast } from "sonner";

import type { SupplierShipmentTargetDto } from "@semo/api-contracts";

import { useMallShipments } from "../hooks/useMallShipments";

/** 이 화면이 등록하는 상태. 준비중으로 잡아 두는 경우가 드물어 「출고」를 기본으로 둔다. */
const REGISTER_STATUS = "SHIPPED" as const;

interface OrderCardProps {
  target: SupplierShipmentTargetDto;
  couriers: { code: string; name: string }[];
  /**
   * 등록 함수를 **부모에서 받는다.** 카드마다 `useMallShipments()` 를 부르면 훅 인스턴스가
   * 주문 수만큼 생긴다 — 질의는 react-query 가 합쳐 주지만 뮤테이션 상태는 카드마다
   * 따로 돌아, 한 장을 등록하는 동안 다른 카드가 멀쩡히 눌린다.
   */
  onRegister: (input: {
    orderNo: string;
    data: {
      courierCode?: string;
      trackingNo?: string;
      status: typeof REGISTER_STATUS;
      items: { itemId: string; quantity: number }[];
    };
  }) => Promise<unknown>;
  isRegistering: boolean;
  onRegistered: () => void | Promise<void>;
}

/**
 * 주문 한 건 — 남은 품목과 송장 등록 폼.
 *
 * 수량 기본값을 **남은 수량 전부**로 잡는다. 부분 출고는 기본이지만 «전부 보내는 것» 이
 * 대다수라, 매번 숫자를 채우게 하면 그 대다수가 손해를 본다.
 */
const OrderCard: React.FC<OrderCardProps> = ({
  target,
  couriers,
  onRegister,
  isRegistering,
  onRegistered,
}) => {
  const remaining = useMemo(
    () =>
      target.items.map((item) => ({
        ...item,
        remaining: item.quantity - item.shippedQuantity,
      })),
    [target.items],
  );

  const [courierCode, setCourierCode] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(remaining.map((item) => [item.id, item.remaining])),
  );

  const selected = remaining
    .map((item) => ({ itemId: item.id, quantity: quantities[item.id] ?? 0 }))
    .filter((line) => line.quantity > 0);

  const overShipped = remaining.some(
    (item) => (quantities[item.id] ?? 0) > item.remaining,
  );

  const handleSubmit = async () => {
    if (selected.length === 0) {
      toast.error("보낼 품목의 수량을 입력하세요.");
      return;
    }
    if (overShipped) {
      toast.error("남은 수량보다 많이 보낼 수 없습니다.");
      return;
    }
    // 송장번호를 적었으면 택배사도 있어야 한다 — 서버도 같은 규칙으로 막지만, 그 전에
    // 화면에서 알려 주는 편이 왕복 한 번을 아낀다.
    if (trackingNo.trim() && !courierCode) {
      toast.error("송장번호를 적었으면 택배사도 고르세요.");
      return;
    }

    try {
      await onRegister({
        orderNo: target.orderNo,
        data: {
          courierCode: courierCode || undefined,
          trackingNo: trackingNo.trim() || undefined,
          status: REGISTER_STATUS,
          items: selected,
        },
      });
      toast.success(`${target.orderNo} 송장을 등록했습니다.`);
      setTrackingNo("");
      await onRegistered();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "송장 등록에 실패했습니다.",
      );
    }
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <strong className="text-base font-semibold tracking-tight text-slate-900">
          {target.orderNo}
        </strong>
        <span className="text-xs text-slate-500">
          {new Intl.DateTimeFormat("ko-KR", {
            dateStyle: "medium",
            timeZone: "Asia/Seoul",
          }).format(new Date(target.orderedAt))}
        </span>
      </div>

      <p className="mt-1.5 flex items-start gap-1.5 text-sm text-slate-600">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          {target.shipTo.name}
          <span className="block text-xs text-slate-500">
            ({target.shipTo.zip}) {target.shipTo.address}
          </span>
        </span>
      </p>

      <ul className="mt-3 divide-y divide-slate-100">
        {remaining.map((item) => (
          <li
            key={item.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5"
          >
            <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
              {item.name}
              {item.spec && (
                <span className="ml-1.5 text-xs text-slate-500">
                  {item.spec}
                </span>
              )}
            </span>

            <span className="shrink-0 text-xs text-slate-500">
              주문 {item.quantity}
              {item.unit ?? ""}
              {/* 이미 보낸 수량이 있으면 그것도 보여 준다 — 부분 출고에서 「왜 남은
                  수량이 이것뿐인가」가 화면에 설명돼 있어야 한다. */}
              {item.shippedQuantity > 0 && (
                <span className="ml-1 text-slate-400">
                  · 보냄 {item.shippedQuantity}
                </span>
              )}
            </span>

            <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-600">
              보낼 수량
              <input
                type="number"
                min={0}
                max={item.remaining}
                value={quantities[item.id] ?? 0}
                onChange={(event) =>
                  setQuantities((prev) => ({
                    ...prev,
                    [item.id]: Number(event.target.value),
                  }))
                }
                className="h-9 w-20 rounded-lg border border-slate-200 px-2 text-right text-sm"
              />
            </label>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <select
          value={courierCode}
          onChange={(event) => setCourierCode(event.target.value)}
          className="h-10 min-w-[9rem] cursor-pointer rounded-lg border border-slate-200 px-2 text-sm"
          aria-label="택배사"
        >
          <option value="">택배사 선택</option>
          {couriers.map((courier) => (
            <option key={courier.code} value={courier.code}>
              {courier.name}
            </option>
          ))}
        </select>

        <input
          value={trackingNo}
          onChange={(event) => setTrackingNo(event.target.value)}
          placeholder="송장번호 (선택)"
          className="h-10 min-w-[10rem] flex-1 rounded-lg border border-slate-200 px-3 text-sm"
          aria-label="송장번호"
        />

        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={isRegistering || selected.length === 0 || overShipped}
          className="bg-primary hover:bg-primary-hover h-10 shrink-0 cursor-pointer rounded-lg px-4 text-sm font-semibold text-primary-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Truck className="mr-1.5 inline h-4 w-4" />
          출고 등록
        </button>
      </div>

      {overShipped && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          남은 수량보다 많이 보낼 수 없습니다.
        </p>
      )}
    </div>
  );
};

/**
 * 몰 배송 탭 — 공급사가 보낼 주문 목록.
 *
 * **목록이 비어 있는 것과 「몰 주문을 받은 적이 없는 것」은 다르다.** 전자는 다 보냈다는
 * 뜻이라 문구를 가른다 — 여기서 「주문이 없습니다」라고만 쓰면 공급사는 주문이 사라진 줄 안다.
 */
export const MallShipmentList: React.FC = () => {
  const {
    targets,
    couriers,
    isLoading,
    error,
    refresh,
    register,
    isRegistering,
  } = useMallShipments();

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} className="h-40 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-2 p-10 text-center">
        <AlertCircle className="h-6 w-6 text-red-500" />
        <p className="text-sm text-slate-600">
          배송 목록을 불러오지 못했습니다.
        </p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="text-primary cursor-pointer text-sm font-semibold hover:underline"
        >
          다시 시도
        </button>
      </div>
    );
  }

  if (targets.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 p-10 text-center">
        <Package className="h-6 w-6 text-slate-300" />
        <p className="text-sm text-slate-600">보낼 몰 주문이 없습니다.</p>
        <p className="text-xs text-slate-500">
          몰 주문이 견적 공고로 올라가 마감되고, 내 견적으로 계약이 서면 여기에
          나타납니다. 이미 보낸 주문은 목록에서 빠집니다.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4">
      {targets.map((target) => (
        <OrderCard
          key={target.orderNo}
          target={target}
          couriers={couriers}
          onRegister={register}
          isRegistering={isRegistering}
          onRegistered={refresh}
        />
      ))}
    </div>
  );
};
